/**
 * socket.io connection to the API for live updates. Connected while signed
 * in; the auth callback reads the current access token on every reconnect.
 *
 * The server refuses an expired access token ("unauthenticated"), and
 * socket.io does not retry after a refusal. Access tokens last 15 minutes,
 * so an app opened later would otherwise get no live updates at all (photos
 * stuck on "Processing…"). On a refusal we refresh the token and connect
 * again; `ensureRealtime` reconnects when the app comes back to the front.
 */
import { io, type Socket } from 'socket.io-client'

let socket: Socket | null = null

export function connectRealtime(
  getToken: () => string | null,
  /** Refresh the access token; resolves false when the session has ended. */
  refresh: () => Promise<boolean>,
): Socket {
  socket?.disconnect()
  const origin = import.meta.env.VITE_SOCKET_URL || undefined
  const s = io(origin ?? '', {
    path: '/socket.io',
    transports: ['websocket'],
    auth: (cb) => cb({ token: getToken() }),
  })
  let retrying = false
  s.on('connect_error', (err) => {
    // Other errors (no signal) are retried by socket.io itself.
    if (err.message !== 'unauthenticated' || retrying) return
    retrying = true
    void refresh()
      .then((ok) => {
        if (ok && socket === s) s.connect()
      })
      .finally(() => {
        retrying = false
      })
  })
  socket = s
  return s
}

/** Reconnect if the connection dropped (the app was in the background, or a login was refused). */
export function ensureRealtime() {
  if (socket && !socket.connected) socket.connect()
}

export function disconnectRealtime() {
  socket?.disconnect()
  socket = null
}

export const realtime = () => socket
